/**
 * RetrievalProgressInitialManager
 *
 * Headless component (renders null) that owns the SSE subscription for
 * INITIAL_RETRIEVAL (first-time metadata fetch).
 *
 * Mount once in AppContent BEFORE <Router> so the connection survives
 * route changes (Scenario 4).
 *
 * Responsibilities:
 * 1. Calls useRetrievalProgressInitial() to hold the SSE connection.
 * 2. Registers connect/disconnect in retrievalProgressInitialStore so
 *    any component (e.g. metadata page) can trigger connection after createRetrieval.
 * 3. Syncs SSE state to retrievalProgressInitialStore.
 * 4. Mirrors state into activityStore (metadata_initial_sync) for ActivityDropdown.
 * 5. Auto-connects when selectedOrg.latestMetadataRetrieval is INITIAL_RETRIEVAL
 *    and IN_PROGRESS on page load (Scenario 2).
 */

import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useQueryClient } from '@tanstack/react-query';
import { useRetrievalProgressInitial } from '@/hooks/metadata_v1/useRetrievalProgressInitial';
import { useRetrievalProgressInitialStore } from '@/stores/retrievalProgressInitialStore';
import { subscribeToOrgChange } from '@/lib/org-scope';
import { useActivityStore } from '@/stores/activityStore';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { useSecurityScanStore } from '@/stores/securityScanStore';
import { useIngestJobStore } from '@/stores/ingestJobStore';
import { showRetrievalCompleteToast } from '@/constants/workflow/RetrievalCompleteToast';
import { RETRIEVAL_TOAST_INITIAL } from '@/constants/workflow/retrievalToasts';

const ACTIVITY_TYPE = 'metadata_initial_sync' as const;
const ACTIVE_RETRIEVAL_STATUSES = new Set(['QUEUED', 'IN_PROGRESS']);

export function RetrievalProgressInitialManager() {
  const {
    status,
    message,
    progressPercentage,
    currentPhase,
    totalItems,
    processedItems,
    phaseDetails,
    isConnected,
    isComplete,
    connectionError,
    connect,
    disconnect,
  } = useRetrievalProgressInitial();

  const queryClient = useQueryClient();
  const {
    setConnectFn,
    setDisconnectFn,
    setProgress,
    // Auto-reconnect through the store so it records which organization owns the
    // stream (see retrievalProgressInitialStore).
    connect: connectStream,
  } = useRetrievalProgressInitialStore();
  const { startActivity, updateActivity, completeActivity, failActivity, removeActivity } = useActivityStore(
    useShallow((s) => ({
      startActivity: s.startActivity,
      updateActivity: s.updateActivity,
      completeActivity: s.completeActivity,
      failActivity: s.failActivity,
      removeActivity: s.removeActivity,
    }))
  );
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const user = useAuthStoreV1((state) => state.user);
  const checkOngoingJobAndRehydrate = useSecurityScanStore((s) => s.checkOngoingJobAndRehydrate);
  const checkIngestJobAndRehydrate = useIngestJobStore((s) => s.checkOngoingJobAndRehydrate);

  const activeActivityIdRef = useRef<string | null>(null);
  const observedActiveRetrievalIdRef = useRef<string | null>(null);
  const completedNotifiedIdRef = useRef<string | null>(null);

  useEffect(() => {
    setConnectFn(connect);
  }, [connect, setConnectFn]);

  useEffect(() => {
    setDisconnectFn(disconnect);
  }, [disconnect, setDisconnectFn]);

  useEffect(() => {
    setProgress({
      status,
      message,
      progressPercentage,
      currentPhase,
      totalItems,
      processedItems,
      phaseDetails: phaseDetails ?? null,
      isConnected,
      isComplete,
      connectionError,
    });
  }, [
    status,
    message,
    progressPercentage,
    currentPhase,
    totalItems,
    processedItems,
    phaseDetails,
    isConnected,
    isComplete,
    connectionError,
    setProgress,
  ]);

  // -------------------------------------------------------------------------
  // Poll GetMetadataTypes every 1 minute while stream is live
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isConnected || isComplete) return;

    // Refetch immediately on connect so short-lived retrievals (<30s) still get a refresh
    queryClient.refetchQueries({ queryKey: ['metadataTypes'] });
    const intervalId = setInterval(() => {
      queryClient.refetchQueries({ queryKey: ['metadataTypes'] });
    }, 30_000);

    return () => clearInterval(intervalId);
  }, [isConnected, isComplete, queryClient]);

  useEffect(() => {
    if (!isConnected) return;
    const retrievalId = useRetrievalProgressInitialStore.getState().retrievalId;
    if (!retrievalId) return;
    // Already tracked by the auto-reconnect below — restarting the activity here
    // would reset its progress and start time.
    if (activeActivityIdRef.current === retrievalId) return;
    activeActivityIdRef.current = retrievalId;
    startActivity(retrievalId, ACTIVITY_TYPE, 'Starting initial fetch...');
  }, [isConnected, startActivity]);

  useEffect(() => {
    const id = activeActivityIdRef.current;
    if (!id) return;

    if (isComplete) {
      completedNotifiedIdRef.current = id;
      completeActivity(id, message ?? 'Completed');
      setTimeout(() => removeActivity(id), 3_000);
      activeActivityIdRef.current = null;
      // Invalidate user-data so useOrg hydrates with new retrievalId; GetMetadataTypes then runs (it depends on retrievalId)
      queryClient.invalidateQueries({ queryKey: ['user-data'] });

      showRetrievalCompleteToast(RETRIEVAL_TOAST_INITIAL);

      // Backend may have triggered a security scan or ingest after retrieval; connect to SSE if there's an ongoing job
      const orgId = selectedOrg?.id;
      const userId = user?.id;
      if (orgId != null && userId != null) {
        void checkOngoingJobAndRehydrate(String(orgId), String(userId));
        void checkIngestJobAndRehydrate(String(orgId), String(userId));
      }
      return;
    }

    if (status === 'in_progress') {
      updateActivity(id, {
        progress: progressPercentage,
        message: message ?? undefined,
      });
    }
  }, [
    progressPercentage,
    message,
    status,
    isComplete,
    updateActivity,
    completeActivity,
    queryClient,
    selectedOrg?.id,
    user?.id,
    checkOngoingJobAndRehydrate,
    checkIngestJobAndRehydrate,
  ]); // queryClient kept — still used for refetchQueries / invalidateQueries in this effect

  useEffect(() => {
    if (!connectionError) return;
    const id = activeActivityIdRef.current;
    if (!id) return;
    failActivity(id, connectionError);
    activeActivityIdRef.current = null;
  }, [connectionError, failActivity]);

  const autoConnectedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const retrieval = selectedOrg?.latestMetadataRetrieval;
    if (!retrieval) return;
    if (isConnected) return;

    const isInitial =
      retrieval.retrievalType === 'INITIAL_RETRIEVAL' &&
      ACTIVE_RETRIEVAL_STATUSES.has(retrieval.status);

    if (isInitial) {
      observedActiveRetrievalIdRef.current = retrieval.id;
      if (autoConnectedIdRef.current === retrieval.id) return;
      autoConnectedIdRef.current = retrieval.id;
      activeActivityIdRef.current = retrieval.id;
      startActivity(retrieval.id, ACTIVITY_TYPE, 'Reconnecting...');
      connectStream(retrieval.id);
    }
  }, [
    selectedOrg?.latestMetadataRetrieval?.id,
    selectedOrg?.latestMetadataRetrieval?.status,
    selectedOrg?.latestMetadataRetrieval?.retrievalType,
    isConnected,
    connectStream,
    startActivity,
  ]);

  // Organization switch — park the initial fetch we were following and resume
  // the one owned by the organization we are entering. The store has already
  // aborted the stream; see the same effect in RetrievalProgressRefreshManager
  // for why the resume happens here rather than in the reconnect effect above.
  useEffect(
    () =>
      subscribeToOrgChange((nextOrgId) => {
        activeActivityIdRef.current = null;
        autoConnectedIdRef.current = null;
        observedActiveRetrievalIdRef.current = null;
        completedNotifiedIdRef.current = null;

        const parkedId = useRetrievalProgressInitialStore
          .getState()
          .takeParkedRetrievalId(nextOrgId);
        if (!parkedId) return;

        activeActivityIdRef.current = parkedId;
        autoConnectedIdRef.current = parkedId;
        observedActiveRetrievalIdRef.current = parkedId;

        // Resumes the parked entry (progress and start time are preserved for an
        // activity that is already in progress) or recreates it if it has gone.
        startActivity(parkedId, ACTIVITY_TYPE, 'Reconnecting...');

        connectStream(parkedId);
        setProgress({ status: 'in_progress', message: 'Reconnecting...' });
      }),
    [connectStream, setProgress, startActivity]
  );

  useEffect(() => {
    const retrieval = selectedOrg?.latestMetadataRetrieval;
    if (!retrieval) return;
    if (retrieval.retrievalType !== 'INITIAL_RETRIEVAL') return;

    if (ACTIVE_RETRIEVAL_STATUSES.has(retrieval.status)) {
      observedActiveRetrievalIdRef.current = retrieval.id;
      return;
    }

    if (retrieval.status !== 'COMPLETED') return;
    if (completedNotifiedIdRef.current === retrieval.id) return;

    const activeId = activeActivityIdRef.current;
    const storeRetrievalId = useRetrievalProgressInitialStore.getState().retrievalId;
    const sawThisRun =
      observedActiveRetrievalIdRef.current === retrieval.id ||
      activeId === retrieval.id ||
      storeRetrievalId === retrieval.id;

    if (!sawThisRun) return;

    completedNotifiedIdRef.current = retrieval.id;

    if (activeId === retrieval.id) {
      completeActivity(retrieval.id, 'Completed');
      setTimeout(() => removeActivity(retrieval.id), 3_000);
      activeActivityIdRef.current = null;
    }

    queryClient.invalidateQueries({ queryKey: ['user-data'] });
    showRetrievalCompleteToast(RETRIEVAL_TOAST_INITIAL);

    const orgId = selectedOrg?.id;
    const userId = user?.id;
    if (orgId != null && userId != null) {
      void checkOngoingJobAndRehydrate(String(orgId), String(userId));
      void checkIngestJobAndRehydrate(String(orgId), String(userId));
    }
  }, [
    selectedOrg?.latestMetadataRetrieval?.id,
    selectedOrg?.latestMetadataRetrieval?.status,
    selectedOrg?.latestMetadataRetrieval?.retrievalType,
    selectedOrg?.id,
    user?.id,
    completeActivity,
    checkOngoingJobAndRehydrate,
    checkIngestJobAndRehydrate,
  ]);

  return null;
}
